import { NewsletterPopup } from 'theme';

// This module changes presentation only. Shopify continues to save consent.
const root = document.documentElement;
const dialogTabindexBefore = new Map();

// Show focus indicators only when the customer uses keyboard navigation.
document.addEventListener('keydown', event => {
  if (event.key === 'Tab') root.classList.add('cosytas-keyboard-focus');
}, true);
document.addEventListener('pointerdown', () => {
  root.classList.remove('cosytas-keyboard-focus');
}, true);

function focusPrivacyDialog(dialog) {
  if (!dialogTabindexBefore.has(dialog)) {
    dialogTabindexBefore.set(dialog, dialog.getAttribute('tabindex'));
    dialog.setAttribute('tabindex', '-1');
  }
  dialog.focus({ preventScroll: true });
}

function restoreDialogTabindex(dialog) {
  if (!dialogTabindexBefore.has(dialog)) return;
  const previous = dialogTabindexBefore.get(dialog);
  if (previous === null) dialog.removeAttribute('tabindex');
  else dialog.setAttribute('tabindex', previous);
  dialogTabindexBefore.delete(dialog);
}
const body = document.body;
const inertBefore = new Map();
let activeDialog = null;
let previousFocus = null;
let scrollPosition = 0;
let banner = null;
let preferences = null;
let bannerModalBefore = null;

function visible(element) {
  return Boolean(element && !element.hidden && element.getClientRects().length &&
    getComputedStyle(element).visibility !== 'hidden');
}

function focusables(dialog) {
  return [...dialog.querySelectorAll('a[href],button,input,select,textarea,[tabindex]')]
    .filter(element => !element.disabled && element.tabIndex >= 0 && visible(element));
}

function focusDialog() {
  if (activeDialog && !activeDialog.contains(document.activeElement)) {
    focusPrivacyDialog(activeDialog);
  }
}

function syncBackground() {
  for (const [element, wasInert] of inertBefore) {
    if (!activeDialog || element.parentElement !== body || element.contains(activeDialog)) {
      if (element.inert !== wasInert) element.inert = wasInert;
      inertBefore.delete(element);
    }
  }
  if (!activeDialog) return;
  for (const element of body.children) {
    if (element.contains(activeDialog) || /^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(element.tagName)) continue;
    if (!inertBefore.has(element)) inertBefore.set(element, element.inert);
    if (!element.inert) element.inert = true;
  }
}

function syncPrivacy() {
  const nextBanner = document.getElementById('shopify-pc__banner');
  const nextPreferences = document.getElementById('shopify-pc__prefs');
  if (nextBanner !== banner || nextPreferences !== preferences) {
    dialogObserver.disconnect();
    banner = nextBanner;
    preferences = nextPreferences;
    for (const element of [banner, preferences]) {
      if (element) dialogObserver.observe(element, {
        attributes: true, attributeFilter: ['style', 'hidden', 'class'], childList: true
      });
    }
  }

  const prefsDialog = document.getElementById('shopify-pc__prefs__dialog');
  const prefsOpen = visible(preferences) && visible(prefsDialog);
  // Only the preferences-to-banner transition needs a style change before measuring.
  if (!prefsOpen && root.classList.contains('cosytas-privacy-preferences')) {
    root.classList.remove('cosytas-privacy-preferences');
  }
  const nextDialog = prefsOpen ? prefsDialog : (visible(banner) ? banner : null);
  const wasOpen = Boolean(activeDialog);
  const dialogChanged = nextDialog !== activeDialog;
  // Start on the dialog itself, without selecting a link or button.
  const shouldFocusDialog = dialogChanged && nextDialog;
  if (dialogChanged && activeDialog) restoreDialogTabindex(activeDialog);

  if (!wasOpen && nextDialog) {
    previousFocus = document.activeElement;
    scrollPosition = window.scrollY;
    root.style.setProperty('--cosytas-scroll-offset', `${-scrollPosition}px`);
    bannerModalBefore = banner?.getAttribute('aria-modal') ?? null;
  }
  activeDialog = nextDialog;
  root.classList.toggle('cosytas-privacy-preferences', prefsOpen);
  root.classList.toggle('cosytas-privacy-open', Boolean(activeDialog));
  syncBackground();

  if (activeDialog) {
    if (banner && banner.getAttribute('aria-modal') !== 'true') banner.setAttribute('aria-modal', 'true');
    if (shouldFocusDialog) focusPrivacyDialog(activeDialog);
  } else if (wasOpen) {
    root.style.removeProperty('--cosytas-scroll-offset');
    if (banner) {
      if (bannerModalBefore === null) banner.removeAttribute('aria-modal');
      else banner.setAttribute('aria-modal', bannerModalBefore);
    }
    window.scrollTo({ top: scrollPosition, behavior: 'instant' });
    if (previousFocus?.isConnected && !previousFocus.closest('[inert]')) {
      previousFocus.focus({ preventScroll: true });
    }
  }
  if (wasOpen !== Boolean(activeDialog)) {
    document.dispatchEvent(new CustomEvent('cosytas:privacy-visibility', {
      detail: { open: Boolean(activeDialog) }
    }));
  }
}

const dialogObserver = new MutationObserver(syncPrivacy);
// Shopify inserts/removes its banner and preferences directly in the body.
new MutationObserver(records => {
  const changedElements = records.flatMap(record => [...record.addedNodes, ...record.removedNodes])
    .filter(node => node.nodeType === 1);
  const privacyChanged = changedElements.some(element =>
    element.id === 'shopify-pc__banner' || element.id === 'shopify-pc__prefs' ||
    element.querySelector('#shopify-pc__banner, #shopify-pc__prefs'));
  const stylesChanged = changedElements.some(element => /^(STYLE|LINK)$/.test(element.tagName));
  if (privacyChanged || stylesChanged) syncPrivacy();
  else if (activeDialog && changedElements.some(element => !/^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(element.tagName))) {
    // New app content must be locked immediately, without remeasuring the dialog.
    syncBackground();
  }
}).observe(body, { childList: true });
document.addEventListener('focusin', focusDialog);
document.addEventListener('keydown', event => {
  if (!activeDialog || event.key !== 'Tab') return;
  const items = focusables(activeDialog);
  const first = items[0];
  const last = items[items.length - 1];
  if (!first) return;
  if (document.activeElement === activeDialog) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
    return;
  }
  if (event.shiftKey && (document.activeElement === first || !activeDialog.contains(document.activeElement))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (document.activeElement === last || !activeDialog.contains(document.activeElement))) {
    event.preventDefault();
    first.focus();
  }
}, true);
window.addEventListener('pageshow', syncPrivacy);
syncPrivacy();

class CosytasNewsletterPopup extends NewsletterPopup {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('cosytas:privacy-visibility', event => {
      clearTimeout(this.privacyDelay);
      if (event.detail.open && this.open) {
        this.waitingForPrivacy = true;
        this.hide();
      } else if (!event.detail.open && this.waitingForPrivacy) {
        this.privacyDelay = setTimeout(() => this.show(), 3000);
      }
    }, { signal: this.abortController.signal });
  }

  show(animate = true) {
    if (activeDialog) {
      this.waitingForPrivacy = true;
      return Promise.resolve();
    }
    this.waitingForPrivacy = false;
    return super.show(animate);
  }

  disconnectedCallback() {
    clearTimeout(this.privacyDelay);
    super.disconnectedCallback();
  }
}

if (!customElements.get('cosytas-newsletter-popup')) {
  customElements.define('cosytas-newsletter-popup', CosytasNewsletterPopup);
}
